import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Badge, Table, Alert, ProgressBar } from 'react-bootstrap';
import { Doughnut, Line, Bar, PolarArea } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, LineElement, PointElement, LineController, BarController, DoughnutController, PolarAreaController, CategoryScale, LinearScale, BarElement, Tooltip, Filler, Legend } from 'chart.js';
import { taskService, userService, departmentService } from '../../services/api';
import './Analytics.css';
import TaskTrend from './TaskTrend';
import TaskCompletion from './TaskCompletion';

// Register required Chart.js components
ChartJS.register(
  ArcElement, 
  LineElement, 
  PointElement, 
  LineController, 
  BarController,
  DoughnutController,
  PolarAreaController,
  CategoryScale, 
  LinearScale,
  BarElement,
  Tooltip, 
  Filler,
  Legend
);

const EmployeeAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeTasks, setEmployeeTasks] = useState([]);
  const [timeRange, setTimeRange] = useState('month');
  const [error, setError] = useState(null);
  const [timeStats, setTimeStats] = useState({
    averageCompletionTime: 0,
    fastestTask: { time: 0, name: '' },
    slowestTask: { time: 0, name: '' }
  });
  const [periodStats, setPeriodStats] = useState({
    total: 0,
    completed: 0,
    completionRate: 0
  });

  // Load initial data on mount
  useEffect(() => {
    fetchData();
  }, []);

  // Load selected employee data when employee, department or time range changes
  useEffect(() => {
    if (selectedEmployee) {
      fetchEmployeeDetails();
    }
  }, [selectedEmployee, timeRange]);

  // Filter users when department changes
  useEffect(() => {
    if (selectedDepartment === 'all') {
      setFilteredUsers(users);
    } else if (selectedDepartment) {
      const filtered = users.filter(user => user.department_id === parseInt(selectedDepartment));
      setFilteredUsers(filtered);
      
      // If current selected employee is not in this department, reset selection
      if (selectedEmployee) {
        const employee = users.find(u => u.id === parseInt(selectedEmployee));
        if (employee && employee.department_id !== parseInt(selectedDepartment)) {
          setSelectedEmployee(filtered.length > 0 ? filtered[0].id : null);
        }
      }
    }
  }, [selectedDepartment, users]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get all employees with analytics data
      const employeesResponse = await taskService.getEmployeeAnalytics();
      
      if (employeesResponse?.data?.success) {
        const employeesData = employeesResponse.data.data;
        setUsers(employeesData);
        setFilteredUsers(employeesData);
        
        // Get unique departments from employees
        const uniqueDepartments = [...new Set(employeesData.map(emp => emp.department_id))];
        const departmentsData = uniqueDepartments.map(deptId => ({
          id: deptId,
          name: employeesData.find(emp => emp.department_id === deptId)?.department_name || 'Unknown'
        }));
        setDepartments(departmentsData);
        
        // Select first employee by default
        if (employeesData.length > 0) {
          setSelectedEmployee(employeesData[0].id);
        }
      } else {
        setError('Failed to load employee data');
      }
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load data. Please check your server connection.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await taskService.getEmployeeDetails(selectedEmployee, timeRange);
      
      if (response?.data?.success) {
        const data = response.data.data;
        setEmployeeData(data);
        setEmployeeTasks(data.tasks || []);
        
        // Calculate time stats for tasks
        calculateTimeStats(data.tasks || []);
        // Calculate period stats for tasks
        calculatePeriodStats(data.tasks || []);
      } else {
        setError('Failed to load employee details');
      }
    } catch (err) {
      console.error('Error fetching employee details:', err);
      setError('Failed to load employee details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const calculateTimeStats = (tasks) => {
    // Filter completed tasks with valid dates
    const completedTasks = tasks.filter(task => 
      task.status === 'completed' && 
      task.created_at && 
      task.updated_at &&
      new Date(task.created_at).getTime() &&
      new Date(task.updated_at).getTime()
    );
    
    if (completedTasks.length === 0) {
      setTimeStats({
        averageCompletionTime: 0,
        fastestTask: { time: 0, name: 'No completed tasks', id: null },
        slowestTask: { time: 0, name: 'No completed tasks', id: null }
      });
      return;
    }
    
    // Calculate completion times (in days) for each task
    const taskCompletionTimes = completedTasks.map(task => {
      const createdDate = new Date(task.created_at);
      const completedDate = new Date(task.updated_at);
      const timeDiff = completedDate.getTime() - createdDate.getTime();
      const daysDiff = Math.max(0, timeDiff / (1000 * 60 * 60 * 24)); // Convert ms to days, minimum 0
      
      return {
        id: task.id,
        title: task.title || 'Untitled Task',
        days: Math.round(daysDiff * 10) / 10, // Round to 1 decimal place
        priority: task.priority
      };
    });
    
    // Calculate average completion time
    const totalDays = taskCompletionTimes.reduce((sum, task) => sum + task.days, 0);
    const averageDays = totalDays / taskCompletionTimes.length;
    
    // Find fastest and slowest tasks
    taskCompletionTimes.sort((a, b) => a.days - b.days);
    const fastest = taskCompletionTimes[0];
    const slowest = taskCompletionTimes[taskCompletionTimes.length - 1];
    
    setTimeStats({
      averageCompletionTime: Math.round(averageDays * 10) / 10,
      fastestTask: { 
        time: fastest.days,
        name: fastest.title,
        id: fastest.id,
        priority: fastest.priority
      },
      slowestTask: { 
        time: slowest.days,
        name: slowest.title,
        id: slowest.id,
        priority: slowest.priority
      }
    });
  };

  const calculatePeriodStats = (tasks) => {
    // Filter by selected time period
    const now = new Date();
    let startDate;
    
    switch(timeRange) {
      case 'week':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 7);
        break;
      case 'month':
        startDate = new Date(now);
        startDate.setMonth(startDate.getMonth() - 1);
        break;
      case 'quarter':
        startDate = new Date(now);
        startDate.setMonth(startDate.getMonth() - 3);
        break;
      case 'year':
        startDate = new Date(now);
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
      default:
        startDate = new Date(now);
        startDate.setMonth(startDate.getMonth() - 1);
    }
    
    // Tasks in the period
    const periodTasks = tasks.filter(task => {
      if (!task.created_at) return false;
      const createdDate = new Date(task.created_at);
      return createdDate >= startDate && createdDate <= now;
    });
    
    // Completed tasks in the period
    const completedInPeriod = periodTasks.filter(task => 
      task.status === 'completed'
    ).length;
    
    // Calculate completion rate
    const completionRate = periodTasks.length > 0 
      ? Math.round((completedInPeriod / periodTasks.length) * 100)
      : 0;
      
    setPeriodStats({
      total: periodTasks.length,
      completed: completedInPeriod,
      completionRate
    });
  };

  const handleDepartmentChange = (deptId) => {
    setSelectedDepartment(deptId);
  };

  const handleEmployeeChange = (empId) => {
    setSelectedEmployee(empId);
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  // Task status distribution chart
  const getStatusData = () => {
    if (!employeeData) return {
      labels: [],
      datasets: [{ data: [], backgroundColor: [], borderColor: [], borderWidth: 1 }]
    };

    return {
      labels: ['Completed', 'In Progress', 'Pending', 'Awaiting Approval'],
      datasets: [
        {
          data: [
            employeeData.completed || 0,
            employeeData.inProgress || 0,
            employeeData.pending || 0,
            employeeData.awaiting || 0
          ],
          backgroundColor: [
            'rgba(25, 135, 84, 0.8)',  // Success green
            'rgba(13, 110, 253, 0.8)', // Primary blue
            'rgba(108, 117, 125, 0.8)', // Secondary gray
            'rgba(255, 193, 7, 0.8)'   // Warning yellow
          ],
          borderColor: [
            'rgba(25, 135, 84, 1)',
            'rgba(13, 110, 253, 1)',
            'rgba(108, 117, 125, 1)',
            'rgba(255, 193, 7, 1)'
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  // Task priority breakdown
  const getPriorityData = () => {
    if (!employeeTasks?.length) return {
      labels: [],
      datasets: [{ data: [], backgroundColor: [] }]
    };

    const priorityCounts = employeeTasks.reduce((acc, task) => {
      acc[task.priority] = (acc[task.priority] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: ['High', 'Medium', 'Low', 'Urgent'],
      datasets: [
        {
          data: [
            priorityCounts['high'] || 0,
            priorityCounts['medium'] || 0,
            priorityCounts['low'] || 0,
            priorityCounts['urgent'] || 0
          ],
          backgroundColor: [
            'rgba(255, 193, 7, 0.8)', // Warning yellow for High
            'rgba(108, 117, 125, 0.8)', // Secondary gray for Medium
            'rgba(13, 202, 240, 0.8)', // Info blue for Low
            'rgba(220, 53, 69, 0.8)'  // Danger red for Urgent
          ],
        },
      ],
    };
  };

  // Task completion trend (weekly)
  const getCompletionTrendData = () => {
    // Get last 12 weeks
    const weeks = [];
    const weeklyCompletions = [];
    const now = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - (7 * i));
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      // Format as "MMM DD"
      const startFormatted = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endFormatted = weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      weeks.push(`${startFormatted} - ${endFormatted}`);
      
      // Count completions in this week
      const completionsInWeek = employeeTasks.filter(task => {
        if (task.status !== 'completed' || !task.updated_at) return false;
        const completedDate = new Date(task.updated_at);
        return completedDate >= weekStart && completedDate <= weekEnd;
      }).length;
      
      weeklyCompletions.push(completionsInWeek);
    }
    
    return {
      labels: weeks,
      datasets: [
        {
          label: 'Tasks Completed',
          data: weeklyCompletions,
          borderColor: 'rgba(13, 110, 253, 1)',
          backgroundColor: 'rgba(13, 110, 253, 0.1)',
          tension: 0.4,
          fill: true,
        }
      ],
    };
  };

  // Get workload distribution by day
  const getWorkloadByDayData = () => {
    if (!employeeTasks?.length) return {
      labels: [],
      datasets: []
    };

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const tasksByDay = Array(7).fill(0);
    const completedByDay = Array(7).fill(0);
    
    employeeTasks.forEach(task => {
      if (!task.due_date) return;
      
      const dueDate = new Date(task.due_date);
      if (isNaN(dueDate.getTime())) return; // Skip invalid dates
      
      const dayIndex = dueDate.getDay();
      tasksByDay[dayIndex]++;
      
      if (task.status === 'completed') {
        completedByDay[dayIndex]++;
      }
    });
    
    return {
      labels: days,
      datasets: [
        {
          label: 'Total Tasks',
          data: tasksByDay,
          backgroundColor: 'rgba(13, 110, 253, 0.7)',
          borderColor: 'rgba(13, 110, 253, 1)',
          borderWidth: 1,
        },
        {
          label: 'Completed Tasks',
          data: completedByDay,
          backgroundColor: 'rgba(25, 135, 84, 0.7)',
          borderColor: 'rgba(25, 135, 84, 1)',
          borderWidth: 1,
        }
      ],
    };
  };

  // Task completion trend component
  const TaskTrend = ({ trendData, title }) => {
    const chartData = {
      labels: trendData.map(item => item.date),
      datasets: [
        {
          label: 'Total Tasks',
          data: trendData.map(item => item.total || 0),
          borderColor: 'rgba(13, 202, 240, 1)',
          backgroundColor: 'rgba(13, 202, 240, 0.1)',
          tension: 0.4,
          fill: true,
        },
        {
          label: 'Completed',
          data: trendData.map(item => item.completed || 0),
          borderColor: 'rgba(25, 135, 84, 1)',
          backgroundColor: 'rgba(25, 135, 84, 0.1)',
          tension: 0.4,
          fill: true,
        },
        {
          label: 'Pending',
          data: trendData.map(item => item.pending || 0),
          borderColor: 'rgba(255, 193, 7, 1)',
          backgroundColor: 'rgba(255, 193, 7, 0.1)',
          tension: 0.4,
          fill: true,
        }
      ]
    };

    return (
      <Card className="shadow-sm h-100">
        <Card.Header className="bg-white">
          <h5 className="mb-0">{title}</h5>
        </Card.Header>
        <Card.Body>
          <div style={{ height: '300px' }}>
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      precision: 0
                    }
                  }
                }
              }}
            />
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Update the Completion Time Statistics UI section
  const CompletionTimeStats = () => (
    <Card className="shadow-sm">
      <Card.Body>
        <h5 className="card-subtitle mb-4">
          <i className="fas fa-clock text-primary me-2"></i>
          Completion Time Statistics
        </h5>
        <Row>
          <Col md={4}>
            <div className="stat-box text-center p-3 rounded bg-light">
              <div className="stat-icon mb-2">
                <i className="fas fa-calculator text-primary" style={{ fontSize: '24px' }}></i>
              </div>
              <div className="stat-title text-muted mb-2">Average Time</div>
              <div className="stat-value fw-bold" style={{ fontSize: '24px', color: '#4e73df' }}>
                {timeStats.averageCompletionTime} days
              </div>
              <div className="stat-subtitle text-muted small">
                across all completed tasks
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div className="stat-box text-center p-3 rounded bg-light">
              <div className="stat-icon mb-2">
                <i className="fas fa-bolt text-success" style={{ fontSize: '24px' }}></i>
              </div>
              <div className="stat-title text-muted mb-2">Fastest Task</div>
              <div className="stat-value fw-bold" style={{ fontSize: '24px', color: '#1cc88a' }}>
                {timeStats.fastestTask.time} days
              </div>
              <div className="stat-subtitle small" style={{ 
                maxWidth: '200px', 
                margin: '0 auto',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {timeStats.fastestTask.name}
              </div>
              {timeStats.fastestTask.priority && (
                <Badge bg={
                  timeStats.fastestTask.priority === 'high' ? 'danger' :
                  timeStats.fastestTask.priority === 'medium' ? 'warning' :
                  'info'
                } className="mt-2">
                  {timeStats.fastestTask.priority}
                </Badge>
              )}
            </div>
          </Col>
          <Col md={4}>
            <div className="stat-box text-center p-3 rounded bg-light">
              <div className="stat-icon mb-2">
                <i className="fas fa-hourglass-end text-warning" style={{ fontSize: '24px' }}></i>
              </div>
              <div className="stat-title text-muted mb-2">Slowest Task</div>
              <div className="stat-value fw-bold" style={{ fontSize: '24px', color: '#f6c23e' }}>
                {timeStats.slowestTask.time} days
              </div>
              <div className="stat-subtitle small" style={{ 
                maxWidth: '200px', 
                margin: '0 auto',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {timeStats.slowestTask.name}
              </div>
              {timeStats.slowestTask.priority && (
                <Badge bg={
                  timeStats.slowestTask.priority === 'high' ? 'danger' :
                  timeStats.slowestTask.priority === 'medium' ? 'warning' :
                  'info'
                } className="mt-2">
                  {timeStats.slowestTask.priority}
                </Badge>
              )}
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  if (loading && !employeeData) {
    return (
      <Container className="py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading employee data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <h1 className="analytics-title mb-4">
        <i className="fas fa-chart-line text-primary me-2"></i> 
        Employee Performance Analytics
      </h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Department</Form.Label>
                <Form.Select 
                  value={selectedDepartment}
                  onChange={(e) => handleDepartmentChange(e.target.value)}
                >
                  <option value="all">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Employee</Form.Label>
                <Form.Select 
                  value={selectedEmployee || ''}
                  onChange={(e) => handleEmployeeChange(e.target.value)}
                  disabled={filteredUsers.length === 0}
                >
                  {filteredUsers.length === 0 ? (
                    <option value="">No employees available</option>
                  ) : (
                    filteredUsers.map(user => (
                      <option key={user.id} value={user.id}>{user.username}</option>
                    ))
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Time Range</Form.Label>
                <Form.Select
                  value={timeRange}
                  onChange={(e) => handleTimeRangeChange(e.target.value)}
                >
                  <option value="week">Last Week</option>
                  <option value="month">Last Month</option>
                  <option value="quarter">Last Quarter</option>
                  <option value="year">Last Year</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading analytics data...</p>
        </div>
      ) : (
        <>
          {employeeData ? (
            <>
              <div className="employee-profile-header mb-4">
                <Row>
                  <Col md={3}>
                    <div className="employee-profile-card">
                      <div className="employee-avatar-lg">
                        {employeeData.username ? 
                          employeeData.username.substring(0, 2).toUpperCase() : 
                          <i className="fas fa-user"></i>}
                      </div>
                      <h3 className="mt-3 mb-1">{employeeData.username || 'User'}</h3>
                      <p className="text-muted mb-2">{employeeData.email || 'No email'}</p>
                      
                      <Badge bg="primary" className="mx-1">
                        {employeeData.role}
                      </Badge>
                      
                      <div className="mt-3">
                        <div className="department-badge">
                          <i className="fas fa-building me-1"></i>
                          {employeeData.department_name || 'No Department'}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={9}>
                    <Row>
                      <Col md={4}>
                        <Card className="shadow-sm h-100 pulse-card">
                          <Card.Body className="text-center">
                            <div className="stat-icon-container">
                              <i className="fas fa-tasks stat-icon text-primary"></i>
                            </div>
                            <h2 className="display-4 fw-bold">{employeeData.totalTasks || 0}</h2>
                            <p className="text-muted mb-0">Total Tasks</p>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4}>
                        <Card className="shadow-sm h-100 pulse-card">
                          <Card.Body className="text-center">
                            <div className="stat-icon-container">
                              <i className="fas fa-check-circle stat-icon text-success"></i>
                            </div>
                            <h2 className="display-4 fw-bold">{periodStats.completed}</h2>
                            <p className="text-muted mb-0">Completed ({timeRange})</p>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4}>
                        <Card className="shadow-sm h-100 pulse-card">
                          <Card.Body className="text-center">
                            <div className="stat-icon-container">
                              <i className="fas fa-percentage stat-icon text-warning"></i>
                            </div>
                            <h2 className="display-4 fw-bold">{employeeData.completionRate || 0}%</h2>
                            <p className="text-muted mb-0">Completion Rate</p>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                    
                    <Row className="mt-3">
                      <Col>
                        <CompletionTimeStats />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
              
              <Row className="mb-4">
                <Col md={8}>
                  <TaskTrend 
                    trendData={employeeData?.weeklyData?.map(week => ({
                      date: week.period,
                      total: employeeData.totalTasks || 0,
                      completed: week.completionRate || 0,
                      pending: 100 - (week.completionRate || 0)
                    })) || []}
                    title="Task Completion Trend"
                  />
                </Col>
                <Col md={4}>
                  <Card className="shadow-sm h-100">
                    <Card.Header className="bg-white">
                      <h5 className="mb-0">Task Status</h5>
                    </Card.Header>
                    <Card.Body className="d-flex align-items-center justify-content-center">
                      <div style={{ height: '200px', width: '200px' }}>
                        <Doughnut 
                          data={getStatusData()}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'right',
                                labels: {
                                  boxWidth: 12,
                                  font: {
                                    size: 10
                                  }
                                }
                              }
                            },
                            cutout: '70%'
                          }} 
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col md={6}>
                  <Card className="shadow-sm h-100">
                    <Card.Header className="bg-white">
                      <h5 className="mb-0">Workload Distribution (By Day)</h5>
                    </Card.Header>
                    <Card.Body>
                      <div style={{ height: '300px' }}>
                        <Bar 
                          data={getWorkloadByDayData()}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'top',
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                grid: {
                                  display: true,
                                  color: 'rgba(0, 0, 0, 0.05)'
                                },
                                ticks: {
                                  precision: 0
                                }
                              },
                              x: {
                                grid: {
                                  display: false
                                }
                              }
                            }
                          }} 
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="shadow-sm h-100">
                    <Card.Header className="bg-white">
                      <h5 className="mb-0">Priority Distribution</h5>
                    </Card.Header>
                    <Card.Body className="d-flex align-items-center justify-content-center">
                      <div style={{ height: '250px', width: '250px' }}>
                        <PolarArea 
                          data={getPriorityData()}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'right',
                              }
                            }
                          }} 
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              <Card className="shadow-sm">
                <Card.Header className="bg-white">
                  <h5 className="mb-0">Recent Tasks</h5>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table hover className="align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Task</th>
                          <th>Status</th>
                          <th>Priority</th>
                          <th>Due Date</th>
                          <th>Created</th>
                          <th>Completion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employeeTasks.slice(0, 8).map(task => (
                          <tr key={task.id}>
                            <td>
                              <div className="fw-bold">{task.title}</div>
                              <div className="small text-muted">
                                {task.description 
                                  ? (task.description.length > 50 
                                      ? `${task.description.substring(0, 50)}...` 
                                      : task.description) 
                                  : 'No description'}
                              </div>
                            </td>
                            <td>
                              <Badge bg={
                                task.status === 'completed' ? 'success' :
                                task.status === 'in_progress' ? 'primary' :
                                task.status === 'awaiting_approval' ? 'warning' :
                                'secondary'
                              }>
                                {task.status?.replace('_', ' ') || 'Unknown'}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={
                                task.priority === 'high' ? 'danger' :
                                task.priority === 'medium' ? 'warning' :
                                'info'
                              }>
                                {task.priority || 'Normal'}
                              </Badge>
                            </td>
                            <td>
                              {task.due_date 
                                ? new Date(task.due_date).toLocaleDateString() 
                                : 'N/A'}
                            </td>
                            <td>
                              {task.created_at 
                                ? new Date(task.created_at).toLocaleDateString() 
                                : 'N/A'}
                            </td>
                            <td>
                              {task.status === 'completed' ? (
                                <span className="text-success">
                                  <i className="fas fa-check-circle me-1"></i>
                                  {task.updated_at 
                                    ? new Date(task.updated_at).toLocaleDateString() 
                                    : 'N/A'}
                                </span>
                              ) : (
                                <span className="text-muted">Pending</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {employeeTasks.length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              <i className="fas fa-clipboard me-2"></i>
                              No tasks assigned to this employee
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : (
            <Alert variant="info" className="mt-4">
              <i className="fas fa-info-circle me-2"></i>
              Please select an employee to view performance analytics.
            </Alert>
          )}
        </>
      )}
    </Container>
  );
};

export default EmployeeAnalytics; 